
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Injectable, OnDestroy, inject } from "@angular/core";
import { TaskService } from "../task.service";
import { select, Store } from "@ngrx/store";
import { LoadTasks, LoadTasksSuccess, getIsTasksNextPageLoading, getIsTasksIsMoreTasks, getTasksCurrentPageOffset, NextLoadTasks, NextLoadTasksSuccess, getSelectedFilterOption, getIsSearchMode, getSortBy, LoadTasksFail, NextLoadTasksFail, StoreProjectList, StorePriorityList, StoreMemberList, CreateTask, CreateTaskSuccess, CreateTaskFail, RemoveTasksSuccess, RemoveTasksFail, UpdateTask, UpdateTasksSuccess, UpdateTaskFail, UpdateTaskSuccess, StoreAuthUser, getAuthUser, RemoveTasks, AllowLoadMore, CreateTasksSuccess, SetTaskStatisticsInfo, SetIsLoading, getSelectedFilterType, StoreTagList, getTagList, SetSearchStatisticsInfo, SetTagStatisticsInfo, UpdateReminderTasks, getReminderTasks, StoreFolderList, getFolderList, SetListStatisticsInfo, StoreMyTeamUsers, StoreSaveSearches, StoreLocationList, SetLocationStatisticsInfo, getLocationList, getAssignmentTasks, UpdateAssignmentTasks, getMemberList, SetTaskDetailHighlight, getTasks, SetTaskFilterType, SetTaskFilterOption, StoreSettings, getTasksById, getTaskStatisticsInfo, SetSelectAll, ResetSelectedTaskId, SetDetailView, SetEditTaskView, LoadSettings, LoadPriorityList, LoadSaveSearches, FetchTasks, FetchMoreTasks } from "../store";
import { Task } from "../models/task";
import { TaskAttachment } from "../models/task-attachment";
import { TaskComment } from "../models/task-comment";
import { TasksRootState } from "../store/reducers/index";
import { TaskUtils } from "../shared/task-utils";
import { ErrorType, SuccessType, DateFilterType, BulkUpdateIssueType } from "../shared/task-enum";
import { TasksConstants } from "../shared/task-constacts";
import { Observable , of } from "rxjs";
import { SuccessService } from "../../common/providers/success-service";
import { ErrorService } from "../../common/providers/error-service";
import { TranslateService } from "@ngx-translate/core";
import { BulkUpdateArgs, AuthUser, User, FileUpload, TaskStatistics, Project, ICompact, MyUser, PendingOperation } from "../models/index";
import { ConfigService } from "../../common/providers/config.service";
import { Broadcaster } from "../../common/providers/broadcaster.service";
import { MessageTranslatorService } from "../services/message-translator-service";
import { LocalStorageService } from "../services/local-storage.service";
import { Subject, BehaviorSubject } from "rxjs";
import { DatabaseService } from "../services/database.service";
import { FilesStorageService } from "../services/files-storage.service";
import { CableService } from "../services/cable.service";
import { CommonUtil, MediaType, mobileonly } from "../../common/utils/common.utils";
import { AppConstants } from "../../common/utils/app-constants";
import { environment } from "../../../environments/environment";
import { IsDatabaseReady, getIsDeviceReady, getFederatedApps, getUserProfile, getAllContacts } from "../../reducers";
import { TaskNotificationType } from "../components/task-notifications";
import * as _ from "lodash";
import { getOnlineStatus, getLoginStatus } from "../../reducers";
import { Logout } from "../../actions/app";
import { List } from "../models/list";
import { VncLibraryService } from "@uxf/vnc-library";
import { ActivatedRoute, Router } from "@angular/router";
import { Location } from "../models/location";
import { taskAdapter } from "../store/reducers/tasks.reducers";
import { takeUntil, distinctUntilChanged, debounceTime, take, filter, tap, delay, catchError, switchMap, finalize, takeWhile, map } from "rxjs/operators";
import { TaskWatchersComponent } from "../components";
import { DomSanitizer } from "@angular/platform-browser";
import { ElectronService } from "../services/electron.service";
import { ContactInfo } from "src/app/shared/models/contact-info.model";
import { AddVNCContacts, BulkLoadVNCContacts } from "src/app/actions/contact.action";
import { StorageService } from "../../common/services/storage.service";

import { TaskRepositoryStateBase } from "./task.repository-state";

// Mechanical decomposition layer of TaskRepository (Q-02). Behavior-preserving:
// fields/methods are unchanged, split across inheritance layers; the DI token
// remains the final TaskRepository class.
export abstract class TaskRepositorySyncLayer extends TaskRepositoryStateBase {
  // The three pollers below re-arm themselves with setTimeout. Without tracking the
  // handles nothing can stop them, so they keep firing for the life of the page.
  private pollingTimers: any[] = [];

  /** Schedules a poll re-arm and remembers the handle so it can be cancelled. */
  protected schedulePoll(fn: () => void, delayMs: number): void {
    this.pollingTimers.push(setTimeout(fn, delayMs));
  }

  /**
   * Implemented by the ops layer (which owns the pending-* helpers the replay drives).
   * Declared here so initRepo can order the offline replay ahead of the pull without
   * reaching downwards.
   */
  protected abstract replayPendingBulkOperations(): void;

  /** Cancels every pending poll re-arm. Call from ngOnDestroy. */
  public clearPollingTimers(): void {
    this.pollingTimers.forEach(timer => clearTimeout(timer));
    this.pollingTimers = [];
  }

  public syncTasksWithBackend() {
    const offsetZero = 0;

    let lastSyncTaskDate = StorageService.getLocalItem(AppConstants.OFFLINE_TASK_LAST_SYNC_TIMESTAMP);
    // if sync last time exist - load task since it till now.
    // no last sync time - load all tasks

    console.log("[task.repository] syncTasksWithBackend, lastSyncTaskDate: " + lastSyncTaskDate);

    // get latest updated tasks from server
    this.taskService.getTasks(offsetZero, AppConstants.TASKS_SYNC_LIMIT_SERVER, this.getParams(), lastSyncTaskDate)
        .pipe(take(1))
        .subscribe(tasks => {
          // console.log('[task.repository] syncTasksWithBackend tasks from backend: ', tasks);
          if (tasks.length > 0) {
            // save to DB
            this.store.select(IsDatabaseReady).pipe(filter(ready => !!ready), take(1)).subscribe(ready => {
              this.databaseService.saveOrUpdateTasks(tasks).subscribe((res) => {
                // console.log('[task.repository] syncTasksWithBackend, res: ' + res);
                if (res) {
                  StorageService.setLocalItem(AppConstants.OFFLINE_TASK_LAST_SYNC_TIMESTAMP, new Date().toISOString().split(".")[0] + "Z");
                }
              });
            });
          }
          this.processSyncBackendTasksResult();
    });

    if (lastSyncTaskDate !== null) {
      this.taskService.getDeletedTasks(lastSyncTaskDate).pipe(take(1)).subscribe( deletedTasks => {
        if (deletedTasks.length > 0) {
          console.log("[task.repository] syncTasksWithBackend deleted task from backend: ", deletedTasks);
          let selectedIds = [];
          deletedTasks.forEach( deletedTask => {
            selectedIds.push(deletedTask.task_id);
          });
          this.store.select(IsDatabaseReady).pipe(filter(ready => !!ready), take(1)).subscribe(ready => {
            this.databaseService.removeTasks(selectedIds).subscribe(res => {
              this.store.dispatch(new RemoveTasksSuccess({ ids: selectedIds }));
            });
          });
        }
      });
    }
  }

  protected startPollingReminders(): void {
    if (window.appInBackground || !this.isOnline) {
      this.isPollingRemindersStarted = false;
      return;
    }
    this.isPollingRemindersStarted =  true;

    console.log("[task.repository] startPollingReminders:::::", this.shouldPullOn);

    if (!!StorageService.getLocalItem("token") && this.shouldPullOn) {
      this.loadReminderTasks(this.pollingRemindersTimeInterval + 1)
      .pipe(tap(tasks => {
        if (tasks && tasks.length > 0) {
          let reminderTasks = [];
          this.store.select(getReminderTasks).pipe(take(1)).subscribe(data => {
            reminderTasks = data;
          });
          tasks = tasks.filter(t => reminderTasks.findIndex(task => task.id === t.id && t.remind_on === task.remind_on) === -1); // To prevent duplicate tasks
          this.store.dispatch(new UpdateReminderTasks(tasks));
        }
      }), delay(this.pollingRemindersTimeInterval * 1000 * 60))
      .subscribe(() => this.startPollingReminders(), () => this.schedulePoll(() => this.startPollingReminders(), this.pollingRemindersTimeInterval * 1000 * 60));
    } else {
      this.schedulePoll(() => this.startPollingReminders(), this.pollingRemindersTimeInterval * 1000 * 60);
    }
  }

  protected loadReminderTasks(time: number = 5): Observable<Task[]> {
    // console.log('[task.repository] loadReminderTasks');
    return this.taskService.getReminderTasks(time).pipe(take(1));
  }

  protected startPollingTaskUpdate(): void {
    if (window.appInBackground || !this.isOnline) {
      this.isPollingUpdateStarted = false;
      return;
    }
    this.isPollingUpdateStarted =  true;

    console.log("[task.repository] startPollingTaskUpdate:::::", this.shouldPullOn);

    if (!!StorageService.getLocalItem("token") && this.shouldPullOn) {
      this.loadUpdateTasks()
      .pipe(tap(tasks => {
        if (tasks && tasks.length > 0) {
          // Show notifications
          if (tasks && tasks.length > 0) {
            let TasksCreated = [];
            let TasksWatcher = [];
            if ( this.authUser && this.authUser.no_self_notified === "true") {
              tasks = tasks.filter( task => {
                if (task.journals && task.journals.length > 0) {
                  let comment = task.journals.find( comment => comment.created_on === task.updated_on);
                  if (comment) {
                    if (comment.user.id !== this.authUser.id) {
                      if (comment.details && comment.details.length > 0) {
                        let detail = comment.details.find( detail => detail.name === "assigned_to_id");
                        let watcher = comment.details.find( detail => detail.name === "watcher");
                        if (detail && parseInt(detail.new_value) === this.authUser.id) {
                          TasksCreated.push(task);
                        } else if ( watcher && parseInt(watcher.new_value) === this.authUser.id) {
                          TasksWatcher.push(task);
                        } else {
                          return task;
                        }
                      } else {
                        return task;
                      }
                    }
                  }
                } else {
                  if (task.updated_on  === task.created_on ) {
                    if (task.assigned_to && this.authUser && task.assigned_to.id === this.authUser.id) {
                      TasksCreated.push(task);
                    } else if (task.author && task.author.id !== this.authUser.id) {
                      if (task.watchers && task.watchers.length > 0) {
                        let watcher = task.watchers.find( watcher => watcher.id === this.authUser.id );
                        if (watcher) {
                          TasksWatcher.push(task);
                        } else {
                          return task;
                        }
                      } else {
                        return task;
                      }
                    }
                  } else {
                    return task;
                  }
                }
              });
            } else {
              tasks = tasks.filter( task => {
                if (task.updated_on === task.created_on) {
                  if (task.assigned_to && this.authUser && task.assigned_to.id === this.authUser.id) {
                    TasksCreated.push(task);
                  } else if (task.author && task.author.id !== this.authUser.id) {
                    if (task.watchers && task.watchers.length > 0) {
                      let watcher = task.watchers.find( watcher => watcher.id === this.authUser.id );
                      if (watcher) {
                        TasksWatcher.push(task);
                      } else {
                        return task;
                      }
                    } else {
                      return task;
                    }
                  }
                } else {
                  if (task.journals && task.journals.length > 0) {
                    let comment = task.journals.find( comment => comment.created_on === task.updated_on);
                    if (comment.details && comment.details.length > 0) {
                      let detail = comment.details.find( detail => detail.name === "assigned_to_id");
                      let watcher = comment.details.find( detail => detail.name === "watcher");
                      if (detail && parseInt(detail.new_value) === this.authUser.id) {
                        TasksCreated.push(task);
                      } else if ( watcher && parseInt(watcher.new_value) === this.authUser.id) {
                        TasksWatcher.push(task);
                      } else {
                        return task;
                      }
                    } else {
                      return task;
                    }
                  }
                }
              });
            }
            if (tasks.length > 1) {
              tasks.forEach( task => {
                this.store.select(state => getTasksById(state, task.id)).pipe(take(1)).subscribe( oldTask => {
                  if (oldTask) {
                    this.updateTaskOnDBIfNeededAndFireResult(oldTask, new Task(task));
                  }
               });
              });
            } else if (tasks.length === 1 ) {
              this.store.select(state => getTasksById(state, tasks[0].id)).pipe(take(1)).subscribe( oldTask => {
                 if (oldTask) {
                   this.updateTaskOnDBIfNeededAndFireResult(oldTask, new Task(tasks[0]));
                 }
              });
            }

            if (TasksCreated.length > 1) {
              TasksCreated.forEach(task => {
                this.createTaskOnDBIfNeededAndFireResult(new Task(task));
              });

              this.getTasksStats();
              this.getFolderListWithCounters();
              this.getTagsListWithCounters();
              this.getLocationsWithCounters();

            } else if ((TasksCreated.length === 1)) {
              this.createTaskOnDBIfNeededAndFireResult(new Task(TasksCreated[0]));

              this.getTasksStats();
              this.getFolderListWithCounters();
              this.getTagsListWithCounters();
              this.getLocationsWithCounters();
            }

            if (TasksWatcher.length === 1) {
              this.store.select(state => getTasksById(state, TasksWatcher[0].id)).pipe(take(1)).subscribe( oldTask => {
                if (oldTask) {
                  this.updateTaskOnDBIfNeededAndFireResult(oldTask, new Task(TasksWatcher[0]));
                }
             });
            }
          }
        }
      }), delay(this.pollingUpdateTimeInterval * 1000 * 60))
      .subscribe(() => this.startPollingTaskUpdate(), () => this.schedulePoll(() => this.startPollingTaskUpdate(), this.pollingUpdateTimeInterval * 1000 * 60));
    } else {
      this.schedulePoll(() => this.startPollingTaskUpdate(), this.pollingUpdateTimeInterval * 1000 * 60);
    }
  }

  protected startPollingTaskDelete(): void {
    if (window.appInBackground || !this.isOnline) {
      this.isPollingDeleteStarted = false;
      return;
    }
    this.isPollingDeleteStarted =  true;
    if (!!StorageService.getLocalItem("token") && this.shouldPullOn) {
      this.loadDeleteTasks()
      .pipe(tap (deletedTasks => {
        if (deletedTasks.length > 0) {
          console.log("[task.repository] startPollingTaskDelete deleted tasks : ", deletedTasks);
          let selectedIds = [];
          deletedTasks.forEach( deletedTask => {
            selectedIds.push(deletedTask.task_id);
          });
          this.deleteTaskOnDBIfNeededAndFireResult(selectedIds);
        }
      }), delay(this.pollingDeleteTimeInterval * 1000 * 60))
      .subscribe(() => this.startPollingTaskDelete(), () => this.schedulePoll(() => this.startPollingTaskDelete(), this.pollingDeleteTimeInterval * 1000 * 60));
    } else {
      this.schedulePoll(() => this.startPollingTaskDelete(), this.pollingDeleteTimeInterval * 1000 * 60);
    }
  }

  public updateTaskOnDBIfNeededAndFireResult(oldTask: Task, newTask: Task) {
    let removedTagsIds: number[] = [];
    oldTask?.tags?.forEach(oldTag => {
      let tagRemoved = true;
      for (let newTag of (newTask?.tags || [])) {
        if (oldTag.id === newTag.id) {
          tagRemoved = false;
          break;
        }
      }
      if (tagRemoved) {
        removedTagsIds.push(oldTag.id);
      }
    });

    if (CommonUtil.isSQLSupported()) {
      this.store.select(IsDatabaseReady).pipe(filter(ready => !!ready), take(1)).subscribe(ready => {
        this.databaseService.updateTask(newTask, removedTagsIds).subscribe((dbRes) => {
          this.updateTaskIntoStore(oldTask, newTask);
          this.broadcaster.broadcast("updateTaskDetail", newTask.id);
        });
      });
    } else {
      this.updateTaskIntoStore(oldTask, newTask);
      this.broadcaster.broadcast("updateTaskDetail", newTask.id);
    }
  }

  protected createTaskOnDBIfNeededAndFireResult(task: Task) {
    // add on DB
    if (CommonUtil.isSQLSupported()) {
      this.store.select(IsDatabaseReady).pipe(filter(ready => !!ready), take(1)).subscribe(ready => {
        this.databaseService.addNewTask(task).subscribe(res => {
          this.addTaskInStore(task);
        });
      });
    } else {
      this.addTaskInStore(task);
    }
  }

  protected deleteTaskOnDBIfNeededAndFireResult(selectedIds) {
    if (CommonUtil.isSQLSupported()) {
      this.store.select(IsDatabaseReady).pipe(filter(ready => !!ready), take(1)).subscribe(ready => {
        this.databaseService.removeTasks(selectedIds).subscribe(res => {
          this.store.dispatch(new RemoveTasksSuccess({ ids: selectedIds }));
          this.broadcaster.broadcast("deleteTasks");
        });
      });
    } else {
      this.store.dispatch(new RemoveTasksSuccess({ ids: selectedIds }));
      this.broadcaster.broadcast("deleteTasks");
    }
  }

  public stopPullingNotification() {
    if (!environment.isCordova) {
      this.shouldPullOn = false;
    }
  }

  public startPullingNotification() {
    if (!environment.isCordova) {
      this.shouldPullOn = true;
      if (!this.isPollingRemindersStarted) {
        this.startPollingReminders();
      }

      if (!this.isPollingUpdateStarted) {
        this.startPollingTaskUpdate();
      }

      if (!this.isPollingDeleteStarted) {
        this.startPollingTaskDelete();
      }
    }
  }

  protected loadUpdateTasks(): Observable<any[]> {
    // console.log('[task.repository] loadReminderTasks');
    let time: Date = new Date();
    time.setMinutes( time.getMinutes() - this.pollingUpdateTimeInterval );
    return this.taskService.getUpdatedTasks(time).pipe(take(1));
  }

  protected loadDeleteTasks(): Observable<any[]> {
    // console.log('[task.repository] loadReminderTasks');
    let time: Date = new Date();
    time.setMinutes( time.getMinutes() - this.pollingUpdateTimeInterval );
    let deleteAfter = time.toISOString().split(".")[0] + "Z";
    return this.taskService.getDeletedTasks(deleteAfter).pipe(take(1));
  }

  public initRepo() {

    // sync with server every time we have an internet connection
    this.store.select(getOnlineStatus).pipe(distinctUntilChanged(), takeUntil(this.isAlive$)).subscribe((isOnline) => {
      if (this.isLoggedIn) {
        this.syncAuthUser();

        if (isOnline) {
          // console.log('[task.repository] initRepo online');

          // Bulk operations queued while offline live in IndexedDB on every platform, so
          // this replay is not gated on the SQLite handle or on IsDatabaseReady (neither
          // of which ever arrives in the browser build). It is kicked off here, ahead of
          // syncTasksWithBackend below, for the same reason processPendingOperations is:
          // a pull that lands first overwrites the unsent local edit. AppComponent used to
          // run this from its own "online" listener, concurrently with that pull.
          this.replayPendingBulkOperations();

          if (CommonUtil.isSQLSupported()) {
            this.store.select(IsDatabaseReady).pipe(filter(ready => !!ready), take(1)).subscribe(ready => {
              this.store.select(getAuthUser).pipe(filter(aUser => !!aUser), take(1)).subscribe(aUser => {
                // Queued offline edits are pushed first, then server state is pulled.
                // These used to be two independent subscriptions to IsDatabaseReady,
                // so a pull could land before the push and overwrite an unsent local
                // edit with the stale server copy.
                this.processPendingOperations();
                this.syncTasksWithBackend();
              });
            });
          }

          this.getAllUsers();
          this.getSettings();

          if (!environment.isCordova) {
            this.shouldPullOn = true;

            if (!this.isPollingRemindersStarted) {
              this.startPollingReminders();
            }

            if (!this.isPollingUpdateStarted) {
              this.startPollingTaskUpdate();
            }

            if (!this.isPollingDeleteStarted) {
              this.startPollingTaskDelete();
            }
          }

          if (!CommonUtil.isSQLSupported()) {
            // On the SQL path this now runs above, ordered ahead of the pull.
            this.store.select(IsDatabaseReady).pipe(filter(ready => !!ready), take(1)).subscribe(ready => {
              this.processPendingOperations();
            });
          }
          this.store.dispatch(new AllowLoadMore());
        }

        this.syncProjectsListAndMembers();
        this.getTasksStats();
        this.getPriorityList();
        this.getFolderListWithCounters();
        this.getTagsListWithCounters();
        this.getSaveSearches();
        this.getLocationsWithCounters();
      }
    });
  }

  addAllTaskToIndexedDb() {
    const AllStatusRoutes = [
      {
        routingType : "status",
        routingOption: "all"
      },
      {
        routingType: "status",
        routingOption: "open"
      },
      {
        routingType: "status",
        routingOption: "completed"
      },
      {
        routingType: "status",
        routingOption: "new"
      },
      {
        routingType: "status",
        routingOption: "assigned"
      },
      {
        routingType: "status",
        routingOption: "created"
      },
      {
        routingType: "status",
        routingOption: "tomorrow"
      },
      {
        routingType: "status",
        routingOption: "today"
      },
      {
        routingType: "status",
        routingOption: "week"
      },
      {
        routingType: "status",
        routingOption: "watch"
      },
      {
        routingType: "status",
        routingOption: "overdue"
      }
    ];
    for (let taskRoute of AllStatusRoutes) {
      this.getTasks(taskRoute);
    }
  }


  addTagListAndLocationTaskToIndexedDb() {
    this.AllTagListAndLocationTask = this.AllTagListAndLocationTask.reduce((acc, curr) => acc.concat(curr), []);
    if (this.AllTagListAndLocationTask?.length) {
      for (let taskRoute of this.AllTagListAndLocationTask) {
        this.getTasks(taskRoute);
      }
    }
  }
  // Initial tasks load (on start app/search/..)

  /**
   * Entry point only (STR-07): dispatches the request action. TaskFlowEffects is the sole
   * invoker of performGetTasks, so every load is visible in the store's action stream.
   */
  public getTasks(defaultTaskParams?) {
    this.store.dispatch(new FetchTasks({ defaultTaskParams }));
  }

  // method is used when load tasks from DB and there is < AppConstants.TASKS_LIMIT,
  // but more tasks exist on backend side


  protected processSyncBackendTasksResult(){
    this.getTasks();
  }


  public processGetAllTasksResultToDataBase(tasks: Task[], defaultFilter?) {
    let offset = tasks.length;
    let isMoreTasks = false;
    if (tasks.length >= AppConstants.TASKS_LIMIT) {
      isMoreTasks = true;
    } else {
      offset = 0;
      isMoreTasks = false;
    }

    if (tasks.length > 0) {
      this.poppulateAssigneeAvatar(tasks).subscribe((success) => {
        if (success) {
          this.loadTaskToDb(tasks, defaultFilter).subscribe(taskFromDB => {
            if (defaultFilter.routingOption === "overdue") {
              this.addTagListAndLocationTaskToIndexedDb();
              setTimeout(() => {
                this.store.dispatch(new SetIsLoading(false));
              }, 500);
            }
          });
        }
      });
    }
    else {
      this.loadTaskToDb(tasks, defaultFilter).subscribe(taskFromDB => {
        setTimeout(() => {
          this.store.dispatch(new SetIsLoading(false));
        }, 500);
      });
    }
  }

  /** Delegate: body moved to TaskFlowService (STR-07). */
  protected getParams(defaultTaskParams?) {
    return this.flowService.getParams(defaultTaskParams);
  }

  /** Delegate: body moved to TaskFlowService (STR-07). */
  public getSearchQuery() {
    return this.flowService.getSearchQuery();
  }

  /** Entry point only (STR-07): see getTasks. */
  public loadMoreTasks() {
    this.store.dispatch(new FetchMoreTasks());
  }

  // method is used when load more tasks from DB and there is < AppConstants.TASKS_LIMIT,
  // but more tasks exist on backend side



  /** Delegate: body moved to TaskFlowService (STR-07). */
  public poppulateAssigneeAvatar(tasks: Task[]): Observable<boolean> {
    return this.flowService.poppulateAssigneeAvatar(tasks);
  }

  public poppulateCommentersAvatar(task: Task): Observable<boolean> {
    const response = new BehaviorSubject(null);

    if (!task.comments || task.comments.length === 0) {
      console.log("[TaskRepository][poppulateCommentersAvatar] DONE with zero");
      response.next(true);
    } else {
      if (CommonUtil.isSQLSupported()) {
        this.performAvatarsAssignement(task.comments, false, "userAvatar", 3).subscribe(done => {
          console.log("[TaskRepository][poppulateCommentersAvatar] DONE");
          if (environment.isElectron) {
            task.comments.forEach(comment => {
              if (comment.userAvatar) {
                if (CommonUtil.isFileSystemUrl(comment.userAvatar.toString())) {
                  comment.userAvatar = this.sanitizer.bypassSecurityTrustUrl(comment.userAvatar);
                }
              }
            });
          }
          response.next(true);
        });
      } else {
        const currentTimeStamp = new Date().getTime();
        task.comments.map(comment => {
          if (comment.user && this.authUser && comment.user.id === this.authUser.id) {
            comment.userAvatar = this.buildAvatarUrl(this.authUser.jid, currentTimeStamp);
          }
        });
        console.log("[TaskRepository][poppulateCommentersAvatar] DONE");
        response.next(true);
      }
    }

    return response.asObservable().pipe(take(2));
  }

  public poppulateWatchersAvatar(task: Task): Observable<boolean> {
    const response = new BehaviorSubject(null);

    if (!task.watchers || task.watchers.length === 0) {
      console.log("[TaskRepository][poppulateWatchersAvatar] DONE with zero");
      response.next(true);
    } else {
      if (CommonUtil.isSQLSupported()) {
        this.performAvatarsAssignement(task.watchers, false, "avatar", 1).subscribe(done => {
          console.log("[TaskRepository][poppulateWatchersAvatar] DONE");
          if (environment.isElectron) {
            task.watchers.forEach(watcher => {
              if (watcher.avatar) {
                if (CommonUtil.isFileSystemUrl(watcher.avatar.toString())) {
                  watcher.avatar = this.sanitizer.bypassSecurityTrustUrl(watcher.avatar);
                }
              }
            });
          }
          response.next(true);
        });
      } else {
        const currentTimeStamp = new Date().getTime();
        task.watchers.map(watcher => {
          if (watcher && this.authUser && watcher.id === this.authUser.id) {
            watcher.avatar = this.buildAvatarUrl(this.authUser.jid, currentTimeStamp);
          }
        });
        response.next(true);
      }
    }

    return response.asObservable().pipe(take(2));
  }

  public poppulateAllUserAvatar(user: AuthUser) {
    const currentTimeStamp = new Date().getTime();
    this.allUserList$.pipe(take(1)).subscribe(allUsers => {
      allUsers.forEach(user => {
          if (user && this.authUser && user.id === this.authUser.id) {
            user.avatar = this.buildAvatarUrl(this.authUser.email, currentTimeStamp);
          }
      });
      this.setAllUserList(allUsers);
    });
  }

  public poppulateAuthUserAvatar(user: AuthUser, forceResync: boolean): Observable<boolean> {
    console.log("[TaskRepository][poppulateAuthUserAvatar] user: ", user);

    const response = new BehaviorSubject(null);

    if (CommonUtil.isSQLSupported()) {
      this.performAvatarsAssignement([user], forceResync, "userAvatar", 1).subscribe(done => {
        console.log("[TaskRepository][poppulateAuthUserAvatar] DONE");
        response.next(true);
      });
    } else {
      if (forceResync) {
        let timestamp = new Date().getTime();
        user.userAvatar = this.buildAvatarUrl(user.jid, timestamp);
      }
      response.next(true);
    }

    return response.asObservable().pipe(take(2));
  }

  public poppulateMembersAvatar(members: Array<User>, forceResync: boolean): Observable<boolean> {
    console.log("[TaskRepository][poppulateMembersAvatar]", members.length);

    const response = new BehaviorSubject(null);

    if (!members || members.length === 0) {
      console.log("[TaskRepository][poppulateMembersAvatar], DONE with zero");
      response.next(true);
    } else {
      if (CommonUtil.isSQLSupported()) {
        this.performAvatarsAssignement(members, forceResync, "avatar", 1).subscribe(done => {
          console.log("[TaskRepository][poppulateMembersAvatar] DONE");
          response.next(true);
        });
      } else {
        if (forceResync) {
          let timestamp = new Date().getTime();
          members.forEach(member => {
            if ( member && this.authUser && member.id === this.authUser.id ) {
              member.avatar = this.buildAvatarUrl(this.authUser.jid, timestamp);
            }
          });
        }
        response.next(true);
      }
    }

    return response.asObservable().pipe(take(2));
  }

  /** Delegate: body moved to TaskFlowService (STR-07). */
  protected performAvatarsAssignement(objects: any[], forceResync: boolean, avatarPropertyName: string, type: number): Observable<boolean> {
    return this.flowService.performAvatarsAssignement(objects, forceResync, avatarPropertyName, type);
  }



  public serverUrlAvatar(userId: number, currentTimeStamp: number): string {
    let url;
    if (this.isCordovaOrElectron) {
      url = this.taskService.getAvatarURL(userId) + "&timestamp=" + currentTimeStamp;
    } else {
      url = this.taskService.getAvatarURL(userId) + "?timestamp=" + currentTimeStamp;
    }
    return url;
  }

  public setSearchParams(searchParams: any) {
    this.flowService.setSearchParams(searchParams);
  }

  public getSearchParams() {
    return this.flowService.getSearchParamsValue();
  }

  public getTasksStats() {
    console.log("[TaskRepository] getTasksStats");

    let isSearchMode;
    this.store.select(getIsSearchMode).pipe(take(1)).subscribe(o => isSearchMode = o);

    if (isSearchMode) {
      let searchParams = this.getSearchQuery();
      let countParams = TaskUtils.getParamsForStatistics();

      // mobile
      if (CommonUtil.isSQLSupported() && !this.isOnline) {
        this.databaseService.getTaskStatisticsForSearch(searchParams).subscribe( res => {
          this.store.dispatch(new SetSearchStatisticsInfo({ total_count: res.total_count, due_tomorrow_count: res.due_tomorrow_count, overdue_count: res.overdue_count, completed_count: res.completed_count }));
        }, err => {
          // console.log('[OFFLINE] getTaskStatisticsForSearch error...');
          this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
        });
      // web
      } else {
        this.taskService.getSearchStatisticCount(countParams, searchParams).pipe(take(1)).subscribe(res => {
          this.store.dispatch(new SetSearchStatisticsInfo({ total_count: res.total_count, due_tomorrow_count: res.due_tomorrow_count, overdue_count: res.overdue_count, completed_count: res.completed_count }));
        }, err => {
          this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
        });
      }
    } else {
      let routingType;
      this.store.select(getSelectedFilterType).pipe(take(1)).subscribe(o => routingType = o);
      let routingOption;
      this.store.select(getSelectedFilterOption).pipe(take(1)).subscribe(o => routingOption = o);

      // mobile
      if (CommonUtil.isSQLSupported() && !this.isOnline) {
        this.store.select(IsDatabaseReady).pipe(filter(ready => !!ready), take(1)).subscribe(ready => {
          if ( routingType === TasksConstants.ROUTE_TYPE_STATUS ) {
            this.databaseService.getTaskStatistics(this.getParams()).subscribe( res => {
              let taskStatistics: TaskStatistics[] = this.generateTaskStatistics(res);
              this.databaseService.getTaskStatisticsForTaskFilter(this.getParams()).subscribe( res => {
                taskStatistics.forEach( taskStatistic => {
                  if (taskStatistic.type === routingOption ) {
                    taskStatistic.info = {
                      total_count: res.total_count,
                      due_tomorrow_count: res.due_tomorrow_count,
                      overdue_count: res.overdue_count,
                      completed_count: res.completed_count
                    };
                  }
                });
                this.store.dispatch(new SetTaskStatisticsInfo(taskStatistics));
              });
            }, err => {
              // console.log('[OFFLINE] getTaskStatistics error...');
              this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
            });
          } else {
            this.databaseService.getTaskStatisticsForSearch(this.getParams()).subscribe( res => {
              if ( routingType === TasksConstants.ROUTE_TYPE_LIST) {
                this.store.dispatch(new SetListStatisticsInfo({ total_count: res.total_count, due_tomorrow_count: res.due_tomorrow_count, overdue_count: res.overdue_count, completed_count: res.completed_count }));
              } else if ( routingType === TasksConstants.ROUTE_TYPE_TAG) {
                this.store.dispatch(new SetTagStatisticsInfo({ total_count: res.total_count, due_tomorrow_count: res.due_tomorrow_count, overdue_count: res.overdue_count, completed_count: res.completed_count }));
              } else {
                this.store.dispatch(new SetLocationStatisticsInfo({ total_count: res.total_count, due_tomorrow_count: res.due_tomorrow_count, overdue_count: res.overdue_count, completed_count: res.completed_count }));
              }
            }, err => {
              this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
            });
          }
        });

      // web
      } else {
        if ( routingType === TasksConstants.ROUTE_TYPE_TAG ) {
          this.store.select(getTagList).pipe(switchMap( tags => this.getTagStatisitcsInfo()), take(1)).subscribe();
        } else if ( routingType === TasksConstants.ROUTE_TYPE_LIST ) {
          this.store.select(getFolderList).pipe(switchMap( lists => this.getListStatisitcsInfo()), take(1)).subscribe();
        } else if ( routingType === TasksConstants.ROUTE_TYPE_LOCATION ) {
          this.store.select(getLocationList).pipe(switchMap( locations => this.getLocationStatisitcsInfo()), take(1)).subscribe();
        }
        if (this.isOnline) {
          this.taskService.getTaskStatisticsCount().pipe(take(1)).subscribe( res => {
            let taskStatistics: TaskStatistics[] = this.generateTaskStatistics(res);
            this.databaseService.addTaskStats(taskStatistics).subscribe(res => {
              this.store.dispatch(new SetTaskStatisticsInfo(taskStatistics));
            });
          }, err => {
            this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
          });
        } else {
          this.databaseService.getAllStats().subscribe(s => {
            this.store.dispatch(new SetTaskStatisticsInfo(s));
          });
        }
      }
    }
  }

  protected getTagStatisitcsInfo(): Observable<{} | any> {
    let tagParams = this.getParams();
    if (tagParams) {
      let countParams = TaskUtils.getParamsForStatistics();
      return this.taskService.getSearchStatisticCount(countParams, tagParams).pipe(take(1),
      tap(res => {
        this.store.dispatch(new SetTagStatisticsInfo({ total_count: res.total_count, due_tomorrow_count: res.due_tomorrow_count, overdue_count: res.overdue_count, completed_count: res.completed_count }));
      }), catchError( err => {
        this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
        return err;
      }));
    }
    return of();
  }

  public getListStatisitcsInfo(): Observable<{} | any> {
    let listParams = this.getParams();
    if (listParams) {
      let countParams = TaskUtils.getParamsForStatistics();
      return this.taskService.getSearchStatisticCount(countParams, listParams).pipe(take(1),
      tap(res => {
        this.store.dispatch(new SetListStatisticsInfo({ total_count: res.total_count, due_tomorrow_count: res.due_tomorrow_count, overdue_count: res.overdue_count, completed_count: res.completed_count }));
      }), catchError( err => {
        this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
        return err;
      }));
    }
    return of();
  }

  public getLocationStatisitcsInfo(): Observable<{} | any> {
    let locationParams = this.getParams();
    if (locationParams) {
      let countParams = TaskUtils.getParamsForStatistics();
      return this.taskService.getSearchStatisticCount(countParams, locationParams).pipe(take(1),
      tap(res => {
        this.store.dispatch(new SetLocationStatisticsInfo({ total_count: res.total_count, due_tomorrow_count: res.due_tomorrow_count, overdue_count: res.overdue_count, completed_count: res.completed_count }));
      }), catchError( err => {
        this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
        return err;
      }));
    }
    return of();
  }

  public generateTaskStatistics(res: any): TaskStatistics[] {
    let taskStatistics: TaskStatistics[] = [];
    let taskStatistic: TaskStatistics;
    taskStatistic = {
      type: TasksConstants.ROUTE_ALL_TASK,
      info: {
        total_count: res.all_tasks.total_count,
        due_tomorrow_count: res.all_tasks.due_tomorrow_count,
        overdue_count: res.all_tasks.overdue_count,
        completed_count: res.all_tasks.completed_count
      }
    };
    taskStatistics.push(taskStatistic);
    taskStatistic = {
      type: TasksConstants.ROUTE_OPEN,
      info: {
        total_count: res.open.total_count,
        due_tomorrow_count: res.open.due_tomorrow_count,
        overdue_count: res.open.overdue_count,
        completed_count: res.open.completed_count
      }
    };
    taskStatistics.push(taskStatistic);
    taskStatistic = {
      type: TasksConstants.ROUTE_NEW,
      info: {
        total_count: res.new.total_count,
        due_tomorrow_count: res.new.due_tomorrow_count,
        overdue_count: res.new.overdue_count,
        completed_count: res.new.completed_count
      }
    };
    taskStatistics.push(taskStatistic);
    taskStatistic = {
      type: TasksConstants.ROUTE_COMPLETED,
      info: {
        total_count: res.completed.total_count,
        due_tomorrow_count: res.completed.due_tomorrow_count,
        overdue_count: res.completed.overdue_count,
        completed_count: res.completed.completed_count
      }
    };
    taskStatistics.push(taskStatistic);
    taskStatistic = {
      type: TasksConstants.ROUTE_ASSIGNEDTOME,
      info: {
        total_count: res.assigned_to_me.total_count,
        due_tomorrow_count: res.assigned_to_me.due_tomorrow_count,
        overdue_count: res.assigned_to_me.overdue_count,
        completed_count: res.assigned_to_me.completed_count
      }
    };
    taskStatistics.push(taskStatistic);
    taskStatistic = {
      type: TasksConstants.ROUTE_TODAY_DUE,
      info: {
        total_count: res.due_today.total_count,
        due_tomorrow_count: res.due_today.due_tomorrow_count,
        overdue_count: res.due_today.overdue_count,
        completed_count: res.due_today.completed_count
      }
    };
    taskStatistics.push(taskStatistic);
    taskStatistic = {
      type: TasksConstants.ROUTE_TOMORROW_DUE,
      info: {
        total_count: res.due_tomorrow.total_count,
        due_tomorrow_count: res.due_tomorrow.due_tomorrow_count,
        overdue_count: res.due_tomorrow.overdue_count,
        completed_count: res.due_tomorrow.completed_count
      }
    };
    taskStatistics.push(taskStatistic);
    taskStatistic = {
      type: TasksConstants.ROUTE_THIS_WEEK_DUE,
      info: {
        total_count: res.due_this_week_count.total_count,
        due_tomorrow_count: res.due_this_week_count.due_tomorrow_count,
        overdue_count: res.due_this_week_count.overdue_count,
        completed_count: res.due_this_week_count.completed_count
      }
    };
    taskStatistics.push(taskStatistic);
    taskStatistic = {
      type: TasksConstants.ROUTE_CREATEDBYME,
      info: {
        total_count: res.created_by_me.total_count,
        due_tomorrow_count: res.created_by_me.due_tomorrow_count,
        overdue_count: res.created_by_me.overdue_count,
        completed_count: res.created_by_me.completed_count
      }
    };
    taskStatistics.push(taskStatistic);
    taskStatistic = {
      type: TasksConstants.ROUTE_TASKS_I_WATCH,
      info: {
        total_count: res.watched_by_me.total_count,
        due_tomorrow_count: res.watched_by_me.due_tomorrow_count,
        overdue_count: res.watched_by_me.overdue_count,
        completed_count: res.watched_by_me.completed_count
      }
    };
    taskStatistics.push(taskStatistic);
    taskStatistic = {
      type: TasksConstants.ROUTE_MY_OVERDUE_TASKS,
      info: {
        total_count: res.overdue.total_count,
        due_tomorrow_count: res.overdue.due_tomorrow_count,
        overdue_count: res.overdue.overdue_count,
        completed_count: res.overdue.completed_count
      }
    };
    taskStatistics.push(taskStatistic);
    return taskStatistics;
  }

  protected syncProjectsListAndMembers() {
    if (this.isOnline) {
      console.log("[task.repository] syncProjectsListAndMembers online");

      // get project list from server
      this.taskService.getProjects().pipe(take(1)).subscribe(projects => {
        // console.log("[task.repository] syncProjectsListAndMembers got projects from server", projects.length);
        this.store.dispatch(new StoreProjectList(projects));
        this.saveProjectsAndMembersToDB(projects).pipe(take(1)).subscribe((success) => {
          // save project to redux
        });
      }, err => {
        this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
      });
    } else {
      console.log("[task.repository] syncProjectsListAndMembers offline");

      this.store.select(getIsDeviceReady).pipe(filter(ready => !!ready), take(1)).subscribe(ready => {
        // pre-load projects to redux in no connection
        if (CommonUtil.isSQLSupported()) {
          this.store.select(IsDatabaseReady).pipe(filter(ready => !!ready), take(1)).subscribe(ready => {
            this.databaseService.getAllProjects().pipe(take(1)).subscribe((projects) => {
              console.log("[task.repository] syncProjectsListAndMembers, selected from DB", projects.length);
              if (projects.length > 0) {
                this.store.dispatch(new StoreProjectList(projects));
              }
            });
          });
        } else {
          const projects = this.localStorageService.getProjects();
          if (projects && projects.length > 0) {
            this.store.dispatch(new StoreProjectList(projects));
          }
        }
      });
    }
  }

  protected saveProjectsAndMembersToDB(allProjects: Project[]): Observable<boolean>  {
    console.log("[task.repository][saveProjectsAndMembersToDB]");

    const response = new Subject<boolean>();

    // save project to local storage under Web
    if (!CommonUtil.isSQLSupported()) {
      this.localStorageService.storeProjects(allProjects);
    }

    this.getAllMembersFromServer(allProjects).subscribe(allMembersHash => {
      let allMembersFlatArray = [];
      Object.keys(allMembersHash).forEach(k => {
        allMembersFlatArray.push(...allMembersHash[k]);
      });

      console.log("[task.repository][saveProjectsAndMembersToDB] allMembersFlatArray", allMembersFlatArray);

      this.poppulateMembersAvatar(allMembersFlatArray, false).subscribe((success) => {
        if (success) {
          if (CommonUtil.isSQLSupported()) {
            this.store.select(IsDatabaseReady).pipe(filter(ready => !!ready), take(1)).subscribe(ready => {
              this.databaseService.createOrUpdateProjects(allProjects, allMembersHash).subscribe((success) => {
                response.next(true);
              });
            });
          } else {
            allProjects.forEach(project => {
              this.localStorageService.storeMemberList(project.id, allMembersHash[project.id]);
            });

            response.next(true);
          }
        }
      });
    });

    return response.asObservable().pipe(take(1));
  }

  protected getAllMembersFromServer(allProjects: Project[]): Observable<any>  {
    console.log("[task.repository][getAllMembersFromServer] allProjects", allProjects.length);

    const response = new Subject<any>();

    let allMembersHash = {};
    let projectsProcessed: number = 0;

    // retrtieve & collect all members from server
    allProjects.forEach(project => {
      this.taskService.getMembers(project.id).pipe(take(1)).subscribe(members => {
        allMembersHash[project.id] = members;

        ++projectsProcessed;
        if (projectsProcessed === allProjects.length) {
          console.log("[task.repository] getAllMembersFromServer DONE");
          response.next(allMembersHash);
        }
      });
    });

    return response.asObservable().pipe(take(1));
  }

  public getMemberList(projectId: number) {
    console.log("[task.repository] getMemberList projectId", projectId);

    if (CommonUtil.isSQLSupported()) {
      this.store.select(IsDatabaseReady).pipe(filter(ready => !!ready), take(1)).subscribe(ready => {
        this.databaseService.getProjectMembers(projectId).subscribe((members) => {
          if (members && members.length > 0) {
            if (environment.isElectron) {
              members.forEach(member => {
                if (member.avatar) {
                  member.avatar = this.sanitizer.bypassSecurityTrustUrl(member.avatar);
                }
              });
            }
            this.store.dispatch(new StoreMemberList(members));
          }
        });
      });
    } else {
      const members = this.localStorageService.getMemberList(projectId);
      if (members && members.length > 0) {
        this.store.dispatch(new StoreMemberList(members));
      }
    }
  }

  public getTagsListWithCounters() {
    console.log("[task.repository] getTagsListWithCounters");

    if (this.isOnline) {
      this.taskService.getAllTagsWithCount().pipe(take(1)).subscribe(tags => {
        console.log("[task.repository] getTagsListWithCounters tags", tags);
        if (tags?.length) {
          const tagsRoute = tags.map(item => {
            return { routingType: "tags", routingOption: item.name };
          });
          this.AllTagListAndLocationTask.push(tagsRoute);
        }
        this.store.dispatch(new StoreTagList(tags));
      }, err => {
        this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
      });
    } else if (CommonUtil.isSQLSupported()) {
      // get from to DB
      this.store.select(IsDatabaseReady).pipe(filter(ready => !!ready), take(1)).subscribe(ready => {
        this.databaseService.getTagsWithTasksCount().subscribe((tagsCounters) => {
          console.log("[task.repository] getTagsListWithCounters tagsCounters", tagsCounters);
          if (tagsCounters) {
            this.store.dispatch(new StoreTagList(tagsCounters));
          }
        });
      });
    }
  }

  getSearchTags(searchtext: String): Observable<any[]> {
    const response = new Subject<any[]>();

    if (this.isOnline) {
      this.taskService.getSearchTags(searchtext).subscribe(res => {
        response.next(res.tags);
      });
    } else if (CommonUtil.isSQLSupported()) {
      this.databaseService.getSearchTags(searchtext).subscribe(tags => {
        response.next(tags);
      });
    } else {
      response.next(null);
    }

    return response.asObservable().pipe(take(1));
  }

  public getFolderListWithCounters() {
    if (this.isOnline) {
      this.taskService.getLists().pipe(take(1)).subscribe(lists => {
        console.log("[task.repository] getLists lists", lists);
        // save to DB
        if (CommonUtil.isSQLSupported() && lists && lists.length > 0) {
          this.store.select(IsDatabaseReady).pipe(filter(ready => !!ready), take(1)).subscribe(ready => {
            this.databaseService.createOrUpdateLists(lists).subscribe((success) => {
              console.log("[task.repository] lists stored in DB", success);
            });
          });
        }
        if (lists?.length) {
          const listRoute = lists.map(item => {
            return { routingType: "list", routingOption: item.name };
          });
          this.AllTagListAndLocationTask.push(listRoute);
        }
        this.store.dispatch(new StoreFolderList(lists));
      }, err => {
        this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
      });
    } else if (CommonUtil.isSQLSupported()) {
      // get from to DB
      this.store.select(IsDatabaseReady).pipe(filter(ready => !!ready), take(1)).subscribe(ready => {
        this.databaseService.getListsWithTasksCount().subscribe((listCounters) => {
          if (listCounters) {
            this.store.dispatch(new StoreFolderList(listCounters));
          }
        });
      });
    }
  }

  public createList(name: string) {
    // only when Internet is ON
    this.taskService.createList(name).subscribe( res => {
      this.successService.emit({ id: SuccessType.ListCreated, messages: this.msgs.LIST_CREATED });
    }, err => {
      this.errorService.emit({ id: ErrorType.ListError, messages: err });
    });
  }

  public updateList(list: List) {
    // only when Internet is ON
    this.taskService.UpdateList(list).subscribe( res => {
      this.successService.emit({ id: SuccessType.ListUpdated, messages: this.msgs.LIST_UPDATED });
    }, err => {
      this.errorService.emit({ id: ErrorType.LocationError, messages: err });
    });
  }

  public deleteList(list: List) {
    // only when Internet is ON
    this.taskService.deleteList(list).subscribe( res => {
      if (CommonUtil.isSQLSupported()) {
        this.databaseService.deleteList(list.id).subscribe((success) => {
          this.databaseService.removeListFromTasks(list.id).subscribe((success) => {

          });
        });
      }
      this.successService.emit({ id: SuccessType.ListDeleted, messages: this.msgs.LIST_DELETED });
    }, err => {
      this.errorService.emit({ id: ErrorType.ListError, messages: err });
    });
  }

  public getLocationsWithCounters() {
    if (this.isOnline) {
      this.taskService.getLocations().pipe(take(1)).subscribe(locations => {
        console.log("[task.repository] getLocations locations", locations);
        // save to DB
        if (CommonUtil.isSQLSupported() && locations && locations.length > 0) {
          this.store.select(IsDatabaseReady).pipe(filter(ready => !!ready), take(1)).subscribe(ready => {
            this.databaseService.createOrUpdateLocations(locations).subscribe((success) => {
              console.log("[task.repository] locations stored in DB", success);
            });
          });
        }
        if (locations?.length) {
          const locationRoute = locations.map(item => {
            return { routingType: "location", routingOption: item.name };
          });
          this.AllTagListAndLocationTask.push(locationRoute);
        }
        this.store.dispatch(new StoreLocationList(locations));
      }, err => {
        this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
      });
    } else if (CommonUtil.isSQLSupported()) {
      // get from to DB
      this.store.select(IsDatabaseReady).pipe(filter(ready => !!ready), take(1)).subscribe(ready => {
        this.databaseService.getLocationsWithTasksCount().subscribe((locationCounters) => {
          if (locationCounters) {
            this.store.dispatch(new StoreLocationList(locationCounters));
          }
        });
      });
    }
  }

  public createLocation(name: string) {
    // only when Internet is ON
    this.taskService.createLocation(name).subscribe( res => {
      this.successService.emit({ id: SuccessType.LocationCreated, messages: this.msgs.LOCATION_CREATED });
    }, err => {
      this.errorService.emit({ id: ErrorType.LocationError, messages: err });
    });
  }

  public updateLocation(location: Location) {
    // only when Internet is ON
    this.taskService.UpdateLocation(location).subscribe( res => {
      this.successService.emit({ id: SuccessType.LocationUpdated, messages: this.msgs.LOCATION_UPDATED });
    }, err => {
      this.errorService.emit({ id: ErrorType.ListError, messages: err });
    });
  }

  public deleteLocation(location: Location) {
    // only when Internet is ON
    this.taskService.deleteLocation(location).subscribe( res => {
      if (CommonUtil.isSQLSupported()) {
        this.databaseService.deleteLocation(location.id).subscribe((success) => {
          this.databaseService.removeLocationFromTasks(location.id).subscribe((success) => {

          });
        });
      }
      this.successService.emit({ id: SuccessType.LocationDeleted, messages: this.msgs.LOCATION_DELETED });
    }, err => {
      this.errorService.emit({ id: ErrorType.LocationError, messages: err });
    });
  }

  public changeDisplayNamePriorities() {
    this.getPriorityList();
  }

  protected getPriorityList() {
    // The request, the cache fallback and the error policy now live in
    // TaskReferenceEffects (STR-07).
    this.store.dispatch(new LoadPriorityList());
  }

  /** Delegate: body moved to TaskFlowService (STR-07). */
  protected addTaskInStore(task: Task) {
    return this.flowService.addTaskInStore(task);
  }

  /** Delegate: body moved to TaskFlowService (STR-07). */
  public buildAvatarUrl(jid: string, timestamp: number) {
    return this.flowService.buildAvatarUrl(jid, timestamp);
  }

  public getAllUsers() {
    if (this.isOnline) {
      this.taskService.getAllUsers().subscribe(allUsers => {
        this.setAllUserList(allUsers);
        this.databaseService.addContacts(allUsers).subscribe(data => {
          if (allUsers?.length) {
            allUsers.forEach((elem) => {
              try {
                CommonUtil.getBase6ImageFromUrl(elem?.avatar).subscribe(baseImg => {
                  const avatar = {
                    id: elem?.id,
                    data: baseImg,
                    jid: elem.mail
                  };
                  this.databaseService.addAvatar(avatar);
                }, () => {});
              } catch (error) {}
            });
          }
          this.store.dispatch(new BulkLoadVNCContacts(allUsers));
        });
      });
    }
    else {
      this.store.select(getAllContacts).subscribe(res => {
        this.setAllUserList(res);
      });
      this.databaseService.getContacts().subscribe(data => {
        this.setAllUserList(data);
        this.store.dispatch(new BulkLoadVNCContacts(data));
      });
    }
  }


  public getSaveSearches() {
    // Server request, SQLite mirror and the offline fallbacks now live in
    // TaskReferenceEffects (STR-07).
    this.store.dispatch(new LoadSaveSearches());
  }

  public getSettings() {
    // Performed by TaskReferenceEffects (STR-07).
    this.store.dispatch(new LoadSettings());
  }

  /** Delegate: body moved to TaskFlowService (STR-07). */
  loadTaskToDb(task, defaultFilter?): Observable<any> {
    return this.flowService.loadTaskToDb(task, defaultFilter);
  }

  protected processPendingOperations() {
    let pendingOperationsFinishedNumber = 0;
    let pendingCommentOperationItr = 0;
    this.databaseService.getAllPendingOperations().pipe(take(1)).subscribe((ops) => {
      ops.forEach(op => {
        // pending CREATE
        if (op.type === PendingOperation.PENDING_OPERATION_TYPE_TASK_CREATE) {
          console.log("[TaskRepository] pending CREATE", op.task_id);

          this.databaseService.getTask(op.task_id).subscribe((taskToCreate) => {
            if (taskToCreate) {
              console.log("[TaskRepository] pending task found (CREATE)", taskToCreate);

              let fileUploads, repeat;
              if (op.additionalData) {
                fileUploads = op.additionalData["fileUploads"];
                repeat = op.additionalData["repeat"];
                if (repeat) {
                  taskToCreate.repeat = repeat;
                }
              }

              const payload = taskToCreate.toCreatePayload();
              this.taskService.addNewTask(payload).subscribe(taskFromServer => {

                // e.g if we created a task in offline and then completed it
                const isStatusChanged = taskToCreate.status.id !== TaskUtils.statusNewId();
                if (isStatusChanged) {
                  taskFromServer.status = taskToCreate.status;

                  this.taskService.updateTask(null, taskFromServer, null, null).pipe(take(1)).subscribe(serverRes => {
                    console.log("[TaskRepository] pending CREATE, updateTask status done");
                  }, err => {
                    console.error("[TaskRepository] pending CREATE, updateTask status error", err);
                  });
                }

                // a notification to replace a task id in redux
                this.broadcaster.broadcast("replaceTaskId", {"old_id": op.task_id, "new_id": taskFromServer.id});
                this.store.dispatch(new RemoveTasksSuccess({ ids : [op.task_id] }));

                // this.store.dispatch(new CreateTaskSuccess({ task: taskFromServer }));

                // all is good at server side, now replace at DB because we have new ID and cleanup pendings
                this.databaseService.replaceTask(op.task_id, taskFromServer).subscribe(res => {

                  this.databaseService.deletePendingOperation(op.task_id).subscribe(success => {
                    if (success) {
                      console.log("[TaskRepository] pending task cleanup (CREATE) success");
                      ++pendingOperationsFinishedNumber;
                      if (pendingOperationsFinishedNumber === ops.length){
                        this.doAfterPendingFinished();
                      }
                    } else {
                      console.error("[TaskRepository] pending task cleanup (CREATE) error");
                    }
                  });
                });
              }, err => {
                console.error("[TaskRepository] pending addNewTask error", err);
              });
            } else {
              // not found
              this.databaseService.deletePendingOperation(op.task_id).subscribe(success => {
                if (success) {
                  console.log("[TaskRepository] pending task cleanup (CREATE) success");
                  ++pendingOperationsFinishedNumber;
                  if (pendingOperationsFinishedNumber === ops.length){
                    this.doAfterPendingFinished();
                  }
                } else {
                  console.error("[TaskRepository] pending task cleanup (CREATE) error");
                }
              });
            }
          });

        // pending DELETE
        } else if (op.type === PendingOperation.PENDING_OPERATION_TYPE_TASK_DELETE) {
          console.log("[TaskRepository] pending DELETE", op.task_id);

          this.taskService.removeTasks([op.task_id]).pipe(take(1)).subscribe(res => {
            this.databaseService.deletePendingOperation(op.task_id).pipe(take(1)).subscribe(success => {
              if (success) {
                ++pendingOperationsFinishedNumber;
                if (pendingOperationsFinishedNumber === ops.length){
                  this.doAfterPendingFinished();
                }
                console.log("[TaskRepository] pending task cleanup (DELETE) success");
              } else {
                console.error("[TaskRepository] pending task cleanup (DELETE) error");
              }
            });
          }, err => {
            if (err === "Resource Not Found") {
              this.databaseService.deletePendingOperation(op.task_id).pipe(take(1)).subscribe(success => {
                if (success) {
                  ++pendingOperationsFinishedNumber;
                  if (pendingOperationsFinishedNumber === ops.length){
                    this.doAfterPendingFinished();
                  }
                  console.log("[TaskRepository] pending task cleanup (DELETE) success");
                } else {
                  console.error("[TaskRepository] pending task cleanup (DELETE) error");
                }
              });
            } else {
              console.error("[TaskRepository] processPendingOperations, removeTasks error", err);
            }
          });

        // pending DELETE BulkAttachments
        } else if (op.type === PendingOperation.PENDING_OPERATION_TYPE_ATTACHMENTS_BULK_DELETE) {
          console.log("[TaskRepository] pending ATTACHMENTS_BULK_DELETE", op.task_id);

          const attachmentsIds = op.additionalData["attachmentsIds"];

          this.taskService.deleteBulkAttachments(attachmentsIds).pipe(take(1)).subscribe( res => {
            this.databaseService.deletePendingOperation(op.task_id).pipe(take(1)).subscribe(success => {
              if (success) {
                ++pendingOperationsFinishedNumber;
                if (pendingOperationsFinishedNumber === ops.length){
                  this.doAfterPendingFinished();
                }
                console.log("[TaskRepository] pending task cleanup (ATTACHMENTS_BULK_DELETE) success");
              } else {
                console.error("[TaskRepository] pending task cleanup (ATTACHMENTS_BULK_DELETE) error");
              }
            });
          }, err => {
            console.error("[TaskRepository] processPendingOperations, deleteBulkAttachments error", err);
          });

        // pending UPDATE
        } else if (op.type === PendingOperation.PENDING_OPERATION_TYPE_TASK_UPDATE) {
          console.log("[TaskRepository] pending UPDATE", op.task_id);

          this.databaseService.getTask(op.task_id).pipe(take(1)).subscribe((taskToUpdate) => {
            if (taskToUpdate) {
              console.log("[TaskRepository] pending task found (UPDATE)", taskToUpdate);
              let fileUploads, repeat, watcherMembers;
              if (op.additionalData) {
                console.log("[TaskRepository] pending task found (UPDATE) additionalData ", op.additionalData);
                fileUploads = op.additionalData["fileUploads"];
                repeat = op.additionalData["repeat"];
                watcherMembers = op.additionalData["watcherMembers"];
              }

              // const tempTagsIds = taskToUpdate.tags.map(t => t.id > Tag.tempIdMin());

              this.taskService.updateTask(null, taskToUpdate, fileUploads, watcherMembers).pipe(take(1)).subscribe(serverRes => {

                // if (fileUploads && fileUploads.length > 0) {
                  // get the updated task details form server to get proper & complete attachment parameters
                  this.taskService.getTaskDetails(op.task_id).pipe(take(1)).subscribe(updatedTaskFromServer => {
                    // update on DB
                    this.databaseService.saveOrUpdateTasks([updatedTaskFromServer]).pipe(take(1)).subscribe((dbRes) => {
                      this.updateTaskSuccess(taskToUpdate, updatedTaskFromServer);
                    });
                  });
                // }

                this.databaseService.deletePendingOperation(op.task_id).pipe(take(1)).subscribe(success => {
                  if (success) {
                    ++pendingOperationsFinishedNumber;
                    if (pendingOperationsFinishedNumber === ops.length){
                      this.doAfterPendingFinished();
                    }
                    console.log("[TaskRepository] pending task cleanup (UPDATE) success");
                  } else {
                    console.error("[TaskRepository] pending task cleanup (UPDATE) error");
                  }
                });

              }, err => {
                console.error("[TaskRepository] pending updateTask error", err);
              });
            } else {
              console.error("[TaskRepository] pending task not found", op.task_id);
            }
          });
        // add pending comment
        } else if (op.type === PendingOperation.PENDING_OPERATION_TYPE_COMMENT_ADD) {
          console.log("[TaskRepository] pending COMMENT ADD", op.task_id);
          pendingCommentOperationItr += 1;

          this.databaseService.getTask(op.additionalData.task_id).subscribe((taskToUpdate) => {
            if (taskToUpdate) {
              console.log("[TaskRepository] pending task found (UPDATE)", taskToUpdate);
              let taskComment: TaskComment;
              if (op.additionalData) {
                taskComment = new TaskComment(op.additionalData);
              }

              setTimeout(() => {
                this.taskService.addCommentFor(op.additionalData.task_id, taskComment.notes).pipe(take(1)).subscribe( res => {

                  taskToUpdate.comments.filter( comment => comment.id === taskComment.id).map( comment => {
                    comment.id = res.id;
                    comment.user = res.user;
                    comment.created_on = new Date(res.created_on);
                  });
                  taskToUpdate.lock_version = res.lock_version;

                  this.databaseService.saveOrUpdateTasks([taskToUpdate]).pipe(take(1)).subscribe((dbRes) => {
                  });
                },
                err => {
                  console.error("[TaskRepository] pending add comment error", err);
                });

                this.databaseService.deletePendingOperation(op.task_id).subscribe(success => {
                  if (success) {
                    ++pendingOperationsFinishedNumber;
                    if (pendingOperationsFinishedNumber === ops.length){
                      this.doAfterPendingFinished();
                    }
                    console.log("[TaskRepository] pending task cleanup (COMMENT) success");
                  } else {
                    console.error("[TaskRepository] pending task cleanup (COMMENT) error");
                  }
                });
              }, (pendingCommentOperationItr - 1) * 300);
            }
          });
        // update pending comment
        } else if (op.type === PendingOperation.PENDING_OPERATION_TYPE_COMMENT_EDIT) {
          console.log("[TaskRepository] pending COMMENT UPDATE", op.task_id);

          this.databaseService.getTask(op.additionalData.task_id).subscribe((taskToUpdate) => {
            if (taskToUpdate) {
              console.log("[TaskRepository] pending task found (UPDATE)", taskToUpdate);
              let taskComment: TaskComment;
              if (op.additionalData) {
                taskComment = new TaskComment(op.additionalData);
              }

              this.taskService.editCommentFor(taskComment).pipe(take(1)).subscribe( res => {
              }, err => {
                console.error("[TaskRepository] pending update comment error", err);
              });

              this.databaseService.deletePendingOperation(op.task_id).subscribe(success => {
                if (success) {
                  ++pendingOperationsFinishedNumber;
                  if (pendingOperationsFinishedNumber === ops.length){
                    this.doAfterPendingFinished();
                  }
                  console.log("[TaskRepository] pending task cleanup (COMMENT) success");
                } else {
                  console.error("[TaskRepository] pending task cleanup (COMMENT) error");
                }
              });
            }
          });
        }
      });
    });
  }

  public syncAuthUser() {
    console.log("[task.repository] syncAuthUser");

    // got user from local storage
    const user = this.localStorageService.getUser();
    if (user) {
      if (environment.isElectron) {
        if (user.userAvatar) {
          if (CommonUtil.isFileSystemUrl(user.userAvatar.toString())) {
            user.userAvatar = this.sanitizer.bypassSecurityTrustUrl(user.userAvatar);
          }
        }
      }
      this.store.dispatch(new StoreAuthUser(user));
    }

    // sync user from server
    if (this.isOnline) {
      this.taskService.getAuthUser().pipe(take(1)).subscribe(authUser => {
        console.log("[task.repository][syncAuthUser] obtained user: ", authUser);
        const setlang = authUser.language.match(/en|ru|de/) ? authUser.language : "en";
        authUser.language = setlang;
        if (authUser && authUser?.custom_fields) {
          const isIframe = window.self !== window.top;
          if (!isIframe) {
            this.loadDarkMode(authUser?.custom_fields);
          }
        }
        this.loadLanguage(authUser.language);
        this.loadOfficeSetting(authUser?.custom_fields);
        this.poppulateAuthUserAvatar(authUser, true).subscribe(success => {
          if (success) {
            console.log("[task.repository][syncAuthUser] set avatar for user. Not storing to cache");
            this.localStorageService.storeUser(authUser);
            if (environment.isElectron) {
              if (authUser.userAvatar) {
                if (CommonUtil.isFileSystemUrl(authUser.userAvatar.toString())) {
                  authUser.userAvatar = this.sanitizer.bypassSecurityTrustUrl(authUser.userAvatar);
                }
              }
            }
            this.store.dispatch(new StoreAuthUser(authUser));
          }
        });
      }, err => {
        if (!user) {
          this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
        }
      });
    }
  }

  /** Delegate: body moved to TaskFlowService (STR-07). */
  protected updateTaskIntoStore(oldTask: Task, newTask: Task) {
    return this.flowService.updateTaskIntoStore(oldTask, newTask);
  }

  protected doAfterPendingFinished() {
    console.log("[TaskRepository] doAfterPendingFinished");
    this.getTasksStats();
    this.getFolderListWithCounters();
    this.getLocationsWithCounters();
    this.getTagsListWithCounters();
  }

  loadDarkMode(userData) {
    const setting = userData.find((field) => field.name === "dark_mode");
    if (setting) {
      if (setting.value === "1") {
        this.configService.darkMode = true;
        StorageService.setLocalItem(AppConstants.TASK_DARK_MODE, "1");
        const body = document.querySelector("body");
        body.classList.add("active-dark-theme");
      } else {
        this.configService.darkMode = false;
        StorageService.setLocalItem(AppConstants.TASK_DARK_MODE, "0");
        const body = document.querySelector("body");
        body.classList.remove("active-dark-theme");
      }
    }
  }

  public loadLanguage(selectedLanguage) {
    if (!selectedLanguage) {
      selectedLanguage = "en";
    }
    this.translate.use(selectedLanguage);
    this.translate.reloadLang(selectedLanguage);
    this.configService.language = selectedLanguage;
  }

  loadOfficeSetting(userData) {
    const setting = userData.find((field) => field?.name === "officetype" || field?.name === "Office Typ");
    if (setting) {
      if (setting.value === "1") {
        this.configService.officePreview = "COLLAB";
        StorageService.setLocalItem(AppConstants.OFFICE_PREVIEW, "COLLAB");
      } else {
        this.configService.officePreview = "OFC";
        StorageService.setLocalItem(AppConstants.OFFICE_PREVIEW, "OFC");
      }
    }
  }

  /** Delegate: body moved to TaskFlowService (STR-07). */
  updateTaskSuccess(oldTask: Task, newTask: Task) {
    return this.flowService.updateTaskSuccess(oldTask, newTask);
  }


}
